<template>
  <div>
    <!-- Barra de edição — fixa no topo, persiste enquanto o popup está selecionado.
         Independente da visibilidade da imagem; é o "controle remoto" do popup. -->
    <div
      v-if="isPreviewMode && isSelected"
      class="popup-editor-bar popup-editor-bar--fixed"
    >
      <span class="popup-editor-bar__label">
        <span class="popup-editor-bar__dot"></span>
        Pop-Up em edição
      </span>
      <div class="popup-editor-bar__actions">
        <button
          type="button"
          class="popup-editor-bar__btn"
          :title="visible ? 'Re-exibir animação' : 'Mostrar pop-up'"
          @click="reExibir"
        >
          <i :class="visible ? 'fa fa-refresh' : 'fa fa-eye'"></i>
        </button>
        <button
          v-if="visible"
          type="button"
          class="popup-editor-bar__btn"
          title="Ocultar pop-up"
          @click="close"
        >
          <i class="fa fa-eye-slash"></i>
        </button>
      </div>
    </div>

    <!-- Overlay com a imagem do popup. Some quando close() é chamado; em editor,
         a barra acima permite trazê-lo de volta sem precisar mexer na sidebar. -->
    <transition name="popup-fade">
      <div
        v-if="visible"
        class="popup-mask"
        :class="{ 'popup-mask--editor': isPreviewMode }"
      >
        <div
          class="popup-wrapper"
          :style="{
            '--popup-max-w': maxDimensions.w + 'px',
            '--popup-max-h': maxDimensions.h + 'px',
          }"
        >
          <!-- Botão fechar canto — só em modo cliente. -->
          <button
            v-if="!isPreviewMode"
            type="button"
            class="popup-close"
            aria-label="Fechar"
            @click="close"
          >
            ×
          </button>

         <component
            v-if="primaryImage && primaryImageIsClickable && !isPreviewMode"
            :is="getLinkTag(primaryImage)"
            v-bind="getLinkBind(primaryImage)"
            class="popup-link"
            @click="close"
          >
            <img
              :src="primaryImage.src"
              :alt="primaryImage.titulo || ''"
              class="popup-image"
            />
          </component>
          <img
            v-else-if="primaryImage"
            :src="primaryImage.src"
            :alt="primaryImage.titulo || ''"
            class="popup-image"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { isPreview } from "@/utils/tema-preview-listener";
import { detectViewport } from "@/utils/viewport";
import {
  getComponentLinkBind,
  getComponentLinkTag,
  hasComponentLink,
} from "@/utils/componentLink";

// Pop-Up (tipo_id=10) — overlay full-screen com 1 imagem.
//
// Modo CLIENTE: aparece após `delay_seconds`, respeitando `frequency_mode`
// (sempre/sessao/periodo) via sessionStorage/localStorage por componente_id.
//
// Modo EDITOR (iframe do admin com body.tema-preview-highlight): ignora
// delay/frequência/storage. A barra azul fica visível enquanto este popup
// estiver selecionado no admin (via postMessage `tema.selectedComponent`).
// Fechar a imagem (✕) mantém a barra, permitindo re-exibir sem voltar na
// sidebar. Mudar a seleção pra outro componente esconde tudo.

// Espelho do seed `ApiEcommerce/database/migrations/2026_05_07_210254_seed_tipo_popup.php`
// (componente_tipo_atributos para tipo_id=10). Se aquele seed mudar, atualizar aqui também.
// Quando o backend expuser `componente.tipo.atributos` via API, trocar essa constante
// por leitura do payload.
const POPUP_MAX_DIMENSIONS = {
  D: { w: 800, h: 500 }, // DESKTOP_IMG_WIDTH / DESKTOP_IMG_HEIGHT
  T: { w: 500, h: 350 }, // TABLET_IMG_WIDTH / TABLET_IMG_HEIGHT
  M: { w: 320, h: 420 }, // PHONE_IMG_WIDTH / PHONE_IMG_HEIGHT
};

export default {
  name: "PopUpComponent",
  props: {
    componente: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      visible: false,
      isSelected: false,
      timer: null,
      isPreviewMode: false,
      screen: detectViewport(),
    };
  },
  computed: {
    primaryImage() {
      const imgs = this.componente.imagens_validas || [];
      return imgs.length ? imgs[0] : null;
    },
    primaryImageIsClickable() {
      return hasComponentLink(this.primaryImage);
    },
    config() {
      const cfg = this.componente.popup_config || {};
      const mode = ["sempre", "sessao", "periodo"].includes(cfg.frequency_mode)
        ? cfg.frequency_mode
        : "sempre";
      const delay = Math.max(0, parseInt(cfg.delay_seconds, 10) || 0);
      const days =
        mode === "periodo"
          ? Math.max(1, parseInt(cfg.frequency_days, 10) || 1)
          : null;
      return { delay, mode, days };
    },
    storageKey() {
      return `popup_seen_${this.componente.componente_id}`;
    },
    maxDimensions() {
      return POPUP_MAX_DIMENSIONS[this.screen] || POPUP_MAX_DIMENSIONS.D;
    },
  },
  mounted() {
    // Em webview nativo do app DPC, popup overlay sobre navegação por toque
    // confunde o usuário — padrão do projeto é suprimir overlays nesse contexto.
    if (this.$store.getters.isDpcMobileWebView) return;

    window.addEventListener("resize", this.onResize);

    this.isPreviewMode = isPreview();

    if (this.isPreviewMode) {
      window.addEventListener("message", this.onPreviewMessage);
      return;
    }

    if (!this.primaryImage) return;
    if (this.shouldSkip()) return;

    // Pré-carrega a imagem em paralelo ao delay. Em conexão lenta, o browser
    // tem o tempo do delay (geralmente 3-5s) pra terminar antes do popup aparecer.
    this.preloadedImage = new Image();
    this.preloadedImage.src = this.primaryImage.src;

    this.timer = setTimeout(() => {
      const img = this.preloadedImage;
      if (img && img.complete && img.naturalWidth > 0) {
        // Já no cache — mostra direto, sem flash.
        this.show();
        return;
      }
      // Espera o onload; fallback final de 8s pra nunca travar.
      if (img) {
        img.onload = () => this.show();
        img.onerror = () => this.show(); // mostra mesmo em 404 (mantém comportamento atual)
      }
      this.fallbackTimer = setTimeout(() => this.show(), 8000);
    }, this.config.delay * 1000);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    if (this.timer) clearTimeout(this.timer);
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    if (this.preloadedImage) {
      this.preloadedImage.onload = null;
      this.preloadedImage.onerror = null;
    }
    if (this.isPreviewMode) {
      window.removeEventListener("message", this.onPreviewMessage);
    }
  },
  methods: {
    getLinkBind: getComponentLinkBind,
    getLinkTag: getComponentLinkTag,
    onPreviewMessage(e) {
      const data = e && e.data;
      if (!data || data.type !== "tema.selectedComponent") return;
      const match = Number(data.id) === Number(this.componente.componente_id);
      this.isSelected = match;
      this.visible = match;
    },
    reExibir() {
      // Se visível, refaz entrada (fade-out + fade-in). Se oculto, só mostra.
      if (this.visible) {
        this.visible = false;
        setTimeout(() => {
          this.visible = true;
        }, 100);
      } else {
        this.visible = true;
      }
    },
    onResize() {
      this.screen = detectViewport();
    },
    shouldSkip() {
      const { mode, days } = this.config;
      if (mode === "sempre") return false;

      try {
        if (mode === "sessao") {
          return !!window.sessionStorage.getItem(this.storageKey);
        }
        const raw = window.localStorage.getItem(this.storageKey);
        if (!raw) return false;
        const ts = Number(raw);
        if (!Number.isFinite(ts)) return false;
        const expiresAt = ts + days * 24 * 60 * 60 * 1000;
        return Date.now() < expiresAt;
      } catch (_) {
        return false;
      }
    },
    markSeen() {
      const { mode } = this.config;
      if (mode === "sempre") return;
      try {
        const value = String(Date.now());
        if (mode === "sessao") {
          window.sessionStorage.setItem(this.storageKey, value);
        } else {
          window.localStorage.setItem(this.storageKey, value);
        }
      } catch (_) {
        // storage indisponível (anônimo, quota cheia) — degrada silenciosamente.
      }
    },
    close() {
      // Esconde só a imagem. Em editor, a barra fica (isSelected segue true).
      // `markSeen` aqui (e NÃO em show): só consideramos "visto" quando o
      // usuário dismissa ativamente — quem fecha aba/navega antes vai ver de novo.
      // Em editor, frequency/storage são ignorados — não suja o storage.
      this.visible = false;
      if (!this.isPreviewMode) {
        this.markSeen();
      }
    },
    show() {
      // Cancela fallback se ainda pendente. `markSeen` foi movido pra close().
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
      this.visible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
// Cor do editor visual do tema — IDÊNTICA à usada por tema-preview-listener.js
// (outline + label dos body components selecionados). Não trocar sem alinhar lá.
$editor-blue: #1a73e8;
$editor-blue-hover: #1557b0;

.popup-mask {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px) saturate(130%);
  -webkit-backdrop-filter: blur(3px) saturate(130%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.popup-mask--editor {
  background-color: rgba(15, 23, 42, 0.25);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.popup-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

// Contorno tracejado azul indicando "selecionado pra edição" — mesma cor que o
// tema-preview-listener aplica nos body components selecionados.
.popup-mask--editor .popup-wrapper {
  outline: 2px dashed $editor-blue;
  outline-offset: 8px;
  border-radius: 4px;
}

// Barra fixa no topo do viewport, acima do popup-mask. Persiste enquanto o
// popup está selecionado, independente da visibilidade da imagem.
.popup-editor-bar--fixed {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
}

.popup-editor-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: $editor-blue;
  color: #fff;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  min-width: 260px;
}

.popup-editor-bar__label {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.popup-editor-bar__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
}

.popup-editor-bar__actions {
  display: flex;
  gap: 4px;
}

.popup-editor-bar__btn {
  background: transparent;
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.popup-editor-bar__btn:hover {
  background: $editor-blue-hover;
}

.popup-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  color: #333;
  z-index: 1;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.popup-close:hover {
  background: #fff;
}

.popup-link {
  display: block;
  cursor: pointer;
}

.popup-image {
  display: block;
  max-width: var(--popup-max-w, 800px);
  max-height: min(var(--popup-max-h, 500px), 90vh);
  width: auto;
  height: auto;
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.popup-fade-enter,
.popup-fade-leave-to {
  opacity: 0;
  transform: scale(0.96);
}

@media (max-width: 520px) {
  .popup-mask {
    padding: 12px;
  }
  .popup-close {
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    font-size: 18px;
  }
  .popup-editor-bar {
    min-width: auto;
    gap: 12px;
  }
}
</style>
